// The creature page's Name Tag card (shared/nametags.ts): give a creature a
// nickname with a tag from the bag, or take the nickname off for free.
import { NAME_TAG, NAME_TAG_PRICE, NICKNAME_MAX, checkNickname, speciesName } from '../shared/nametags.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { iconUrl } from './assets.ts';
import { esc, full } from './fmt.ts';
import { confirmClick, isArmed } from './confirm.ts';

/** What is typed in each creature's name box, kept across redraws. */
const drafts = new Map<number, string>();
const draftOf = (c: CreatureDTO) => drafts.get(c.id) ?? c.nickname ?? '';

export function nameTagCard(c: CreatureDTO): string {
  const have = S.trainer!.bag[NAME_TAG] ?? 0;
  const draft = draftOf(c);
  const chk = checkNickname(draft);
  const same = chk.ok && chk.name === c.nickname;
  const key = `nick:${c.id}`, armed = isArmed(key);
  const why = c.listed ? 'Listed for sale'
    : !have ? '' : draft.trim() && !chk.ok ? chk.why : '';
  const btn = have
    ? `<button class="btn gold${armed ? ' armed' : ''}" type="submit"${c.listed || !chk.ok || same ? ' disabled' : ''}>${armed ? 'Confirm: use 1 Name Tag' : `${c.nickname ? 'Rename' : 'Name it'} · 1 tag <small>${full(have)}</small>`}</button>`
    : `<a class="btn" href="#/shop/diamonds">Name Tags · ${NAME_TAG_PRICE} diamonds</a>`;
  return `<section class="panel cp-card cp-nick"><div class="cp-h"><img class="cp-ico" src="${iconUrl(NAME_TAG)}" alt=""><h5>Name Tag</h5>${c.nickname ? `<span class="muted">· ${esc(speciesName(c))} called ${esc(c.nickname)}</span>` : ''}</div>
    <form class="row nick-row" data-nick-form="${c.id}" autocomplete="off">
      <input class="grow" type="text" data-nick="${c.id}" maxlength="${NICKNAME_MAX}" value="${esc(draft)}" placeholder="${esc(speciesName(c))}" aria-label="Nickname"${c.listed ? ' disabled' : ''}>
      ${btn}
    </form>
    ${why ? `<p class="cp-why">${esc(why)}</p>` : ''}
    <p class="muted">Comes off if sold</p>
    ${c.nickname && !c.listed ? `<div class="row"><button class="btn small" data-act="nick-clear" data-id="${c.id}">Remove nickname</button></div>` : ''}</section>`;
}

/** Typing in a name box: keep the draft. Returns true when the page should redraw. */
export function nickInput(el: HTMLInputElement): boolean {
  if (el.dataset?.nick === undefined) return false;
  drafts.set(Number(el.dataset.nick), el.value);
  return true;
}

/** The name form was sent (button or Enter). The first press asks to confirm, since a tag is used up. */
export function nickSubmit(form: HTMLFormElement, send: (m: ClientMsg) => void): boolean {
  const id = Number(form.dataset?.nickForm);
  if (!id) return false;
  const c = S.creatures.get(id);
  if (!c) return true;
  const chk = checkNickname(draftOf(c));
  if (!chk.ok || chk.name === c.nickname) return true;
  if (confirmClick(`nick:${id}`)) { drafts.delete(id); send({ t: 'nickname', id, name: chk.name }); }
  return true;
}

/** Take a nickname off (free). */
export function nickClear(id: number, send: (m: ClientMsg) => void) {
  drafts.delete(id);
  send({ t: 'nickname', id, name: null });
}
